import { type FC } from "react";
import { Plus } from "lucide-react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { YearInput } from "@/shared/components/ui/YearInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { EarlierMarriageRow, MarriageEndReason } from "@/shared/types/probateTypes";

export function createEmptyMarriage(): EarlierMarriageRow {
  return {
    id: crypto.randomUUID(),
    firstName: "",
    middleName: "",
    lastName: "",
    endYear: "",
    endReason: "",
  };
}

interface EarlierMarriagesFieldsProps {
  marriages: EarlierMarriageRow[];
  onChange: (index: number, field: keyof EarlierMarriageRow, value: string) => void;
  onAdd: () => void;
  errors?: FlowCustomComponentProps["errors"];
}

export const EarlierMarriagesFields: FC<EarlierMarriagesFieldsProps> = ({
  marriages,
  onChange,
  onAdd,
  errors,
}) => (
  <>
    {marriages.map((m, i) => (
      <div key={m.id} className="flex flex-col gap-3 rounded-lg border border-primary p-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>First name</Label>
            <Input
              placeholder="First name"
              value={m.firstName}
              onChange={(e) => onChange(i, "firstName", e.target.value)}
              className={errors?.[`marriage_${i}_first_name`] ? "border-destructive" : ""}
            />
            <FieldError message={errors?.[`marriage_${i}_first_name`]} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>
              Middle name <span className="text-tertiary">(optional)</span>
            </Label>
            <Input
              placeholder="Middle name"
              value={m.middleName}
              onChange={(e) => onChange(i, "middleName", e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Last name</Label>
          <Input
            placeholder="Last name"
            value={m.lastName}
            onChange={(e) => onChange(i, "lastName", e.target.value)}
            className={errors?.[`marriage_${i}_last_name`] ? "border-destructive" : ""}
          />
          <FieldError message={errors?.[`marriage_${i}_last_name`]} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>What year did the marriage end?</Label>
            <YearInput value={m.endYear} onChange={(v) => onChange(i, "endYear", v)} />
            <FieldError message={errors?.[`marriage_${i}_end_year`]} />
            <p className="text-xs text-secondary">A best guess is fine</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>How did the marriage end?</Label>
            <Select
              value={m.endReason}
              onValueChange={(v) => onChange(i, "endReason", v as MarriageEndReason)}
            >
              <SelectTrigger
                className={errors?.[`marriage_${i}_end_reason`] ? "border-destructive" : ""}
              >
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="divorce">Divorce</SelectItem>
                <SelectItem value="spouse_passed">Spouse passed away</SelectItem>
                <SelectItem value="not_sure">Not sure</SelectItem>
              </SelectContent>
            </Select>
            <FieldError message={errors?.[`marriage_${i}_end_reason`]} />
          </div>
        </div>
      </div>
    ))}

    <button
      type="button"
      onClick={onAdd}
      className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary py-3 text-sm font-medium text-primary hover:bg-subtle"
    >
      <Plus className="size-4" />
      Add another
    </button>
  </>
);
